import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { track, EVENTS } from "../lib/analytics";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Billing() {
  const { refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const pollingRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/billing");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Handle Stripe return with session_id — poll for status
  useEffect(() => {
    const sid = searchParams.get("session_id");
    const canceled = searchParams.get("canceled");
    if (canceled) {
      toast.info("Checkout canceled — no changes to your plan.");
      track(EVENTS.CHECKOUT_ABANDONED);
      setSearchParams({}, { replace: true });
      return;
    }
    if (sid && !pollingRef.current) {
      pollingRef.current = true;
      pollCheckout(sid).finally(() => {
        pollingRef.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollCheckout = async (sessionId) => {
    toast.loading("Confirming your subscription…", { id: "billing-confirm" });
    const maxAttempts = 10;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          await load();
          toast.success("Subscription updated — welcome aboard!", { id: "billing-confirm" });
          track(EVENTS.CHECKOUT_COMPLETED, {
            session_id: sessionId,
            amount: data.amount_total,
            currency: data.currency,
            plan: data.metadata?.plan,
          });
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired.", { id: "billing-confirm" });
          setSearchParams({}, { replace: true });
          return;
        }
      } catch (e) {
        // keep trying
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    toast.message("Payment is still processing. You'll get an email once it's confirmed.", { id: "billing-confirm" });
    setSearchParams({}, { replace: true });
  };

  const startCheckout = async (plan) => {
    setBusy(plan);
    try {
      const origin = window.location.origin;
      track(EVENTS.CHECKOUT_STARTED, { plan });
      const { data } = await api.post("/billing/checkout", { plan, origin_url: origin });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout.");
        setBusy("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      await refresh();
      await load();
      toast.success("Subscription canceled. You can come back anytime.");
      track(EVENTS.SUBSCRIPTION_CANCELED);
    } finally { setBusy(""); }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal");
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't open the billing portal.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open portal.");
    } finally { setBusy(""); }
  };

  if (loading || !data) {
    return <AppLayout><div className="grid place-items-center h-96"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppLayout>;
  }

  const current = data.current_plan;
  const sub = data.subscription;

  return (
    <AppLayout title="Billing" subtitle="Choose the plan that keeps your loved one connected. Upgrade, downgrade, or cancel anytime.">
      <Card className="rounded-3xl border-border/60 soft-shadow mb-6" data-testid="billing-current">
        <CardContent className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Current plan</div>
            <div className="mt-1 flex items-center gap-3">
              <div className="font-display text-2xl font-bold capitalize">{current}</div>
              {sub?.status && (
                <Badge className={
                  sub.status === "active" ? "bg-secondary/15 text-secondary" :
                  sub.status === "trialing" ? "bg-accent text-accent-foreground" :
                  "bg-muted text-muted-foreground"
                }>{sub.status}</Badge>
              )}
            </div>
            {sub?.trial_ends_at && sub.status === "trialing" && (
              <div className="text-sm text-muted-foreground mt-1">Trial ends {new Date(sub.trial_ends_at).toLocaleDateString()}</div>
            )}
            {sub?.current_period_end && sub.status === "active" && (
              <div className="text-sm text-muted-foreground mt-1">Renews {new Date(sub.current_period_end).toLocaleDateString()}</div>
            )}
          </div>
          {current !== "canceled" && (
            <div className="flex gap-3 flex-wrap">
              {data.has_stripe_customer && (
                <Button
                  variant="outline" className="rounded-full" onClick={openPortal}
                  disabled={busy === "portal"} data-testid="billing-manage-button"
                >
                  {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Manage billing
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="billing-cancel-button">Cancel subscription</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                    <AlertDialogDescription>
                      CherishLink will stop scheduling new calls at the end of the current period. Your past summaries stay available. You can reactivate anytime.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep my plan</AlertDialogCancel>
                    <AlertDialogAction onClick={cancel} data-testid="billing-cancel-confirm">Cancel plan</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-3 gap-5">
        {["starter", "plus", "family"].map((id) => {
          const p = data.plans[id];
          const isCurrent = current === id;
          const popular = id === "plus";
          return (
            <Card key={id} className={`rounded-3xl soft-shadow ${popular ? "border-2 border-secondary" : "border-border/60"} ${isCurrent ? "ring-2 ring-primary" : ""}`} data-testid={`billing-plan-${id}`}>
              <CardContent className="p-7">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-semibold">{p.name}</h3>
                  {popular && <Badge className="bg-secondary text-secondary-foreground">Popular</Badge>}
                </div>
                <div className="mt-5 flex items-end gap-1">
                  <div className="font-display text-4xl font-bold">${p.price}</div>
                  <div className="text-muted-foreground mb-1.5">/month</div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">{p.calls}</div>
                <ul className="mt-5 space-y-2.5 min-h-[140px]">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-secondary mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button
                  disabled={isCurrent || busy === id}
                  onClick={() => startCheckout(id)}
                  className={`mt-6 w-full rounded-full ${isCurrent ? "" : popular ? "" : "bg-foreground hover:bg-foreground/90"}`}
                  data-testid={`billing-select-${id}`}
                >
                  {busy === id ? <Loader2 className="h-4 w-4 animate-spin" /> :
                    isCurrent ? <><Check className="h-4 w-4 mr-1.5" /> Current plan</> :
                    current === "canceled" ? "Reactivate" :
                    <><Sparkles className="h-4 w-4 mr-1.5" /> Subscribe</>}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground mt-6 text-center">
        {(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "").startsWith("pk_test_")
          ? "Secure checkout via Stripe. Test mode — use card 4242 4242 4242 4242 for the demo."
          : "Secure checkout via Stripe. Cancel anytime."}
      </p>
    </AppLayout>
  );
}
